import * as React from "react"
import { Head, router, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { Textarea } from "@/components/ui/textarea"
import type { ConsultationDay, PageMeta, PageProps } from "@/types/inertia"
import { useVisitorTimezone, timeInZone, dateTimeInZone } from "@/lib/slot-times"
import { trackConversion, visitSucceeded } from "@/lib/analytics"
import { FormError, useHoneypot } from "@/components/public-form"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import { cn } from "@/lib/utils"

interface Props {
  // Admin feature switch (/admin/availability): when off the slot picker is
  // gone entirely and the page leads with ring-now + request-a-callback.
  slot_booking: boolean
  days: ConsultationDay[]
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    ring_first: { heading?: string; body?: string }
    callback: { heading?: string; body?: string; _hidden?: boolean }
  }
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null
  return <p className="mt-1 text-xs text-danger-display">{message}</p>
}

function telHref(phone: string) {
  return `tel:${phone.replace(/\s/g, "")}`
}

function ContactMethodPicker({
  value,
  onChange,
  name,
  label,
  labels,
}: {
  value: string
  onChange: (v: string) => void
  name: string
  label: string
  labels: { mobile: string; whatsapp: string }
}) {
  return (
    <fieldset>
      <legend>{label}</legend>
      <RadioGroup orientation="horizontal" className="mt-2">
        {[
          { value: "mobile", label: labels.mobile },
          { value: "whatsapp", label: labels.whatsapp },
        ].map((option) => (
          <label key={option.value} className="flex items-center gap-2 font-normal text-ink-body">
            <Radio
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            {option.label}
          </label>
        ))}
      </RadioGroup>
    </fieldset>
  )
}

export default function GiselaBook({ slot_booking, days, content, section_order, meta }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const t = props.translations.consultation
  const locale = props.current_locale || "en"
  const phone = props.current_brand.phone
  // Both the callback form and the cancel flow redirect back to /book with a
  // flash notice — render each in its own spot.
  const callbackNotice = props.flash.notice === t.callback_notice ? props.flash.notice : null
  const generalNotice = props.flash.notice && !callbackNotice ? props.flash.notice : null
  const visitorTz = useVisitorTimezone()

  const [selectedDate, setSelectedDate] = React.useState<string | null>(days[0]?.date ?? null)
  const [selectedSlot, setSelectedSlot] = React.useState<string | null>(null)

  const [name, setName] = React.useState("")
  const [email, setEmail] = React.useState("")
  const [bookingPhone, setBookingPhone] = React.useState("")
  const [contactMethod, setContactMethod] = React.useState("mobile")
  const [note, setNote] = React.useState("")
  const [processing, setProcessing] = React.useState(false)

  const [cbName, setCbName] = React.useState("")
  const [cbPhone, setCbPhone] = React.useState("")
  const [cbEmail, setCbEmail] = React.useState("")
  const [cbMethod, setCbMethod] = React.useState("mobile")
  const [cbNote, setCbNote] = React.useState("")
  const [cbProcessing, setCbProcessing] = React.useState(false)
  const cbHoneypot = useHoneypot("cb-company")

  const selectedDay = days.find((d) => d.date === selectedDate) ?? days[0]
  const selectedSlotObj = selectedDay?.slots.find((s) => s.starts_at === selectedSlot)

  function slotLabel(slot: { starts_at: string; london_label: string }) {
    return visitorTz ? timeInZone(slot.starts_at, visitorTz, locale) : slot.london_label
  }

  function submitBooking(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedSlot) return
    setProcessing(true)
    // Posted to the page's own address rather than "/book": this page has one
    // in each language, and its redirects — the confirmation, and an error
    // back onto the form — follow whichever was used.
    router.post(
      window.location.pathname,
      {
        consultation_booking: {
          starts_at: selectedSlot,
          name,
          email,
          phone: bookingPhone,
          contact_method: contactMethod,
          note,
          client_timezone: visitorTz,
        },
      },
      {
        onSuccess: (page) => {
          if (visitSucceeded(page)) trackConversion("consultation_booked")
        },
        onFinish: () => setProcessing(false),
      }
    )
  }

  function submitCallback(e: React.FormEvent) {
    e.preventDefault()
    setCbProcessing(true)
    router.post(
      "/callback-requests",
      {
        callback_request: {
          name: cbName,
          phone: cbPhone,
          email: cbEmail,
          contact_method: cbMethod,
          note: cbNote,
        },
        ...cbHoneypot.payload,
      },
      {
        onSuccess: (page) => {
          if (visitSucceeded(page)) trackConversion("callback_request")
        },
        onFinish: () => {
          setCbProcessing(false)
          window.location.hash = "callback"
        },
      }
    )
  }

  // The bands below the hero, named by their registry entries so the admin's
  // order applies. Each keeps its own background.
  const bands = presentBands(
    [
      {
        id: "gisela-book-ring-first",
        present: !!(phone && (content.ring_first.heading || content.ring_first.body)),
        render: () => phone && (
                  <section className="bg-cream py-12">
                    <div className="mx-auto max-w-2xl px-6 lg:px-8">
                      <div className="rounded-2xl bg-white p-8 ring-1 ring-hairline">
                        {content.ring_first.heading && <h2>{content.ring_first.heading}</h2>}
                        {content.ring_first.body && (
                          <p className="mt-3 text-ink-muted">{content.ring_first.body}</p>
                        )}
                        <p className="mt-4">
                          <a href={telHref(phone)}>{phone}</a>
                        </p>
                      </div>
                    </div>
                  </section>
        ),
      },
      {
        id: "gisela-book-slots",
        present: slot_booking,
        render: () => (
                <section id="slots" className="bg-cream py-16">
                  <div className="mx-auto max-w-2xl px-6 lg:px-8">
                    <h2>{t.pick_a_time}</h2>
                    <p className="mt-2 text-sm text-ink-muted">
                      {visitorTz ? t.times_local_note.replace("%{tz}", visitorTz) : t.times_uk_note}
                    </p>

                    {days.length === 0 ? (
                      <p className="mt-8 text-ink-muted">{t.no_slots}</p>
                    ) : (
                      <>
                        {/* Day picker */}
                        <div className="mt-6 flex gap-2 overflow-x-auto pb-2">
                          {days.map((day) => (
                            <button
                              key={day.date}
                              type="button"
                              onClick={() => {
                                setSelectedDate(day.date)
                                setSelectedSlot(null)
                              }}
                              className={cn(
                                "shrink-0 rounded-xl px-4 py-2 text-sm ring-1 transition-colors",
                                day.date === selectedDay?.date
                                  ? "bg-teal text-white ring-teal"
                                  : "bg-white text-ink-body ring-hairline hover:bg-teal/5"
                              )}
                            >
                              {day.day_label}
                            </button>
                          ))}
                        </div>

                        {/* Slots for the selected day */}
                        {selectedDay && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-ink-body">{t.slots_this_day}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {selectedDay.slots.map((slot) => (
                                <button
                                  key={slot.starts_at}
                                  type="button"
                                  onClick={() => setSelectedSlot(slot.starts_at)}
                                  className={cn(
                                    "rounded-lg px-4 py-2 text-sm tabular-nums ring-1 transition-colors",
                                    slot.starts_at === selectedSlot
                                      ? "bg-teal text-white ring-teal"
                                      : "bg-white text-ink-body ring-hairline hover:bg-teal/5"
                                  )}
                                >
                                  {slotLabel(slot)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <FieldError message={errors.starts_at} />

                        {/* Details form, revealed once a slot is chosen */}
                        {selectedSlotObj && (
                          <form onSubmit={submitBooking} className="mt-8 rounded-2xl bg-white p-8 ring-1 ring-hairline">
                            <h3>{t.form.heading}</h3>
                            <p className="mt-2 text-sm text-ink-muted">
                              {t.form.selected_slot}{" "}
                              <strong className="text-ink-body">
                                {visitorTz
                                  ? dateTimeInZone(selectedSlotObj.starts_at, visitorTz, locale)
                                  : `${selectedDay.day_label}, ${selectedSlotObj.london_label}`}
                              </strong>
                              {visitorTz && (
                                <>
                                  {" "}
                                  ·{" "}
                                  {t.form.selected_slot_uk.replace(
                                    "%{time}",
                                    `${selectedDay.day_label}, ${selectedSlotObj.london_label}`
                                  )}
                                </>
                              )}{" "}
                              <button
                                type="button"
                                className="text-teal underline"
                                onClick={() => setSelectedSlot(null)}
                              >
                                {t.form.change_time}
                              </button>
                            </p>

                            <div className="mt-6 space-y-5">
                              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <div>
                                  <label htmlFor="bk-name">{t.form.name}</label>
                                  <Input
                                    id="bk-name"
                                    type="text"
                                    required
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    aria-invalid={!!errors.name}
                                    className="mt-1"
                                  />
                                  <FieldError message={errors.name} />
                                </div>
                                <div>
                                  <label htmlFor="bk-email">{t.form.email}</label>
                                  <Input
                                    id="bk-email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    aria-invalid={!!errors.email}
                                    className="mt-1"
                                  />
                                  <FieldError message={errors.email} />
                                </div>
                              </div>

                              <div>
                                <label htmlFor="bk-phone">{t.form.phone}</label>
                                <Input
                                  id="bk-phone"
                                  type="tel"
                                  required
                                  value={bookingPhone}
                                  onChange={(e) => setBookingPhone(e.target.value)}
                                  aria-invalid={!!errors.phone}
                                  className="mt-1"
                                  placeholder="+44 7911 123456"
                                />
                                <p className="mt-1 text-xs text-ink-muted">{t.form.phone_hint}</p>
                                <FieldError message={errors.phone} />
                              </div>

                              <div>
                                <ContactMethodPicker
                                  name="contact_method"
                                  value={contactMethod}
                                  onChange={setContactMethod}
                                  label={t.form.contact_method}
                                  labels={{ mobile: t.form.method_mobile, whatsapp: t.form.method_whatsapp }}
                                />
                                <FieldError message={errors.contact_method} />
                              </div>

                              <div>
                                <label htmlFor="bk-note">{t.form.note}</label>
                                <Textarea
                                  id="bk-note"
                                  rows={4}
                                  value={note}
                                  onChange={(e) => setNote(e.target.value)}
                                  className="mt-1 w-full"
                                />
                              </div>

                              <div className="pt-2">
                                <Button
                                  type="submit"
                                  variant="primary"
                                  size="lg"
                                  disabled={processing}
                                  className="w-full sm:w-auto"
                                >
                                  {processing ? t.form.submitting : t.form.submit}
                                </Button>
                              </div>
                            </div>
                          </form>
                        )}
                      </>
                    )}
                  </div>
                </section>
        ),
      },
      {
        id: "gisela-book-callback",
        present: !sectionHidden(content.callback),
        render: () => (
                <section id="callback" className="bg-white py-16">
                  <div className="mx-auto max-w-2xl px-6 lg:px-8">
                    <h2>{content.callback.heading || props.translations.gisela_pages.book.callback_heading}</h2>
                    {content.callback.body && <p className="mt-3 text-ink-muted">{content.callback.body}</p>}

                    {callbackNotice ? (
                      <div className="mt-8 rounded-2xl bg-cream p-8 text-center ring-1 ring-hairline">
                        <p className="text-ink-body">{callbackNotice}</p>
                      </div>
                    ) : (
                      <form onSubmit={submitCallback} className="mt-8 space-y-5">
                        <FormError message={errors.base} />
                        {cbHoneypot.field}
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                          <div>
                            <label htmlFor="cb-name">{t.callback.name}</label>
                            <Input
                              id="cb-name"
                              type="text"
                              required
                              value={cbName}
                              onChange={(e) => setCbName(e.target.value)}
                              aria-invalid={!!errors.name}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label htmlFor="cb-phone">{t.callback.phone}</label>
                            <Input
                              id="cb-phone"
                              type="tel"
                              required
                              value={cbPhone}
                              onChange={(e) => setCbPhone(e.target.value)}
                              aria-invalid={!!errors.phone}
                              className="mt-1"
                              placeholder="+44 7911 123456"
                            />
                            <FieldError message={errors.phone} />
                          </div>
                        </div>

                        <div>
                          <label htmlFor="cb-email">{t.callback.email}</label>
                          <Input
                            id="cb-email"
                            type="email"
                            value={cbEmail}
                            onChange={(e) => setCbEmail(e.target.value)}
                            aria-invalid={!!errors.email}
                            className="mt-1"
                          />
                          <FieldError message={errors.email} />
                        </div>

                        <div>
                          <ContactMethodPicker
                            name="cb_contact_method"
                            value={cbMethod}
                            onChange={setCbMethod}
                            label={t.callback.contact_method}
                            labels={{ mobile: t.form.method_mobile, whatsapp: t.form.method_whatsapp }}
                          />
                        </div>

                        <div>
                          <label htmlFor="cb-note">{t.callback.note}</label>
                          <Textarea
                            id="cb-note"
                            rows={3}
                            value={cbNote}
                            onChange={(e) => setCbNote(e.target.value)}
                            className="mt-1 w-full"
                          />
                        </div>

                        <div className="pt-2">
                          <Button
                            type="submit"
                            variant="secondary"
                            size="lg"
                            disabled={cbProcessing}
                            className="w-full sm:w-auto"
                          >
                            {cbProcessing ? t.callback.submitting : t.callback.submit}
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                </section>
        ),
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={meta?.title ?? t.meta_title}>
        <meta name="description" content={meta?.description ?? t.meta_description} />
        <meta property="og:title" content={meta?.title ?? t.meta_title} />
        <meta property="og:description" content={meta?.description ?? t.meta_description} />
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
        <HeroBand photo={content.hero.photo} className="max-w-2xl">
            <h1 className="text-white">{content.hero.heading || t.meta_title}</h1>
            {content.hero.subheading && (
              <p className="mt-4 text-lg text-white/80">{content.hero.subheading}</p>
            )}
            {/* The number, and — only while slot booking is on — the other way
                in beside it. With slots off the form below is the page's one
                other ask, and it introduces itself; a second button here just
                said the same thing twice. */}
            {phone && (
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href={telHref(phone)}
                  className="rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline transition-opacity hover:opacity-90"
                >
                  {t.ring_now} · {phone}
                </a>
                {slot_booking && (
                  <>
                    <span className="text-sm text-white/70">{t.or_divider}</span>
                    <a
                      href="#slots"
                      className="rounded-full border border-white/40 px-6 py-2.5 text-sm font-medium text-white no-underline transition-colors hover:bg-white/10"
                    >
                      {t.pick_a_time}
                    </a>
                  </>
                )}
              </div>
            )}
        </HeroBand>
        )}

        {generalNotice && (
          <section className="bg-cream pt-8">
            <div className="mx-auto max-w-2xl px-6 lg:px-8">
              <div className="rounded-2xl bg-white p-6 text-center ring-1 ring-hairline">
                <p className="text-ink-body">{generalNotice}</p>
              </div>
            </div>
          </section>
        )}

        {bands.map((band, i) => (
          <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
        ))}
      </PublicLayout>
    </>
  )
}
